'use client';

import { useMemo, useState } from 'react';
import { track } from '@/lib/telemetry';
import { fmt } from '@/lib/transactions/utils';
import { dayLabel } from '@/lib/transactions/dates';
import type { Transaction } from '@/lib/transactions/types';
import { buildMerchantRows, initials } from '../category-detail/buildMerchantRows';

type SortMode = 'frequency' | 'amount';

interface Props {
  transactions: readonly Transaction[];
  parentCategoryId: string;
}

export function MerchantFrequencyList({ transactions, parentCategoryId }: Props) {
  const [sort, setSort] = useState<SortMode>('frequency');

  const rows = useMemo(() => {
    const base = buildMerchantRows(transactions, parentCategoryId);
    if (sort === 'amount') {
      return base.slice().sort((a, b) => b.total - a.total);
    }
    return base;
  }, [transactions, parentCategoryId, sort]);

  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-surface p-8 text-center text-sm text-text-tertiary">
        Sem comerciantes para esta categoria no período.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div role="tablist" className="inline-flex items-center gap-1 rounded-lg bg-surface-sunken p-1 text-xs">
        <button
          type="button"
          role="tab"
          aria-selected={sort === 'frequency'}
          onClick={() => {
            if (sort !== 'frequency') {
              track('ui.filter_applied', {
                surface: 'merchant_frequency',
                filter: 'sort',
                action: 'set',
                method: 'control',
                value: 'frequency',
              });
            }
            setSort('frequency');
          }}
          className={
            'rounded-md px-3 py-1.5 font-medium transition ' +
            (sort === 'frequency' ? 'bg-surface text-text-primary shadow-sm' : 'text-text-secondary')
          }
        >
          Mais frequentes
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={sort === 'amount'}
          onClick={() => {
            if (sort !== 'amount') {
              track('ui.filter_applied', {
                surface: 'merchant_frequency',
                filter: 'sort',
                action: 'set',
                method: 'control',
                value: 'amount',
              });
            }
            setSort('amount');
          }}
          className={
            'rounded-md px-3 py-1.5 font-medium transition ' +
            (sort === 'amount' ? 'bg-surface text-text-primary shadow-sm' : 'text-text-secondary')
          }
        >
          Maior valor
        </button>
      </div>

      <ul className="overflow-hidden rounded-xl border border-border bg-surface" role="list">
        {rows.map((m) => (
          <li
            key={m.key}
            className="flex items-center gap-3 border-b border-border px-4 py-3 last:border-b-0"
          >
            <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-surface-sunken text-xs font-semibold text-text-secondary">
              {initials(m.name)}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-text-primary">{m.name}</p>
              <p className="text-2xs text-text-tertiary">
                {m.count} {m.count === 1 ? 'transação' : 'transações'} · última {dayLabel(new Date(m.lastDate))}
              </p>
            </div>
            <p className="font-mono text-sm font-semibold text-text-primary tabular-nums">
              {fmt(-m.total)}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}
